import React, { useEffect, useState } from 'react';
import { Popper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { getLanguageDisplayName } from '@/common/utils/translationUtils';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

const MAIN_LANGUAGE_CODE_LIST = Object.keys(TranslationConstant.TRANSLATION_LANG_DISPLAY_JP);

// Elevated z-index so the dropdown renders above the sidebar menu.
const HighZIndexPopper = props => (
    <Popper
        {...props}
        style={{ ...props.style, zIndex: 1400 }}
        className={`${props.className || ''} mainLanguage-autocomplete-popper`}
    />
);

const getLabel = code => (code ? getLanguageDisplayName(code) : '');

/**
 * Single-select searchable dropdown of the main language (主言語), shared by the user / customer
 * management screens. Controlled: value is a language code or '' (empty = not selected).
 * Free text that matches no option is never accepted: on blur it reverts to the selected
 * value, or becomes empty (the parent shows its required error through errorText).
 */
export default function MainLanguageAutocomplete({ value, onChange, errorText, warningText, disabled, className, id }) {
    useTranslationSubscription();
    const [inputValue, setInputValue] = useState(getLabel(value));

    // Keep the text in sync when the parent changes the value (cancel edit, reset row...).
    useEffect(() => {
        setInputValue(getLabel(value));
    }, [value]);

    return (
        <Autocomplete
            id={id}
            disabled={disabled}
            options={MAIN_LANGUAGE_CODE_LIST}
            getOptionLabel={getLabel}
            getOptionSelected={(option, selected) => option === selected}
            value={MAIN_LANGUAGE_CODE_LIST.includes(value) ? value : null}
            inputValue={inputValue}
            onInputChange={(e, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason === 'input' && newInputValue === '') onChange('');
            }}
            onChange={(e, selected) => onChange(selected || '')}
            onBlur={() => {
                if (value && MAIN_LANGUAGE_CODE_LIST.includes(value)) {
                    setInputValue(getLabel(value));
                } else {
                    setInputValue('');
                    onChange('');
                }
            }}
            PopperComponent={HighZIndexPopper}
            popupIcon={<ExpandMoreIcon fontSize="small" />}
            noOptionsText={trI18n('COMMSG001')}
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    className={className || 'form-control form-control-wrapInput form-control-hg40'}
                    error={!!errorText}
                    helperText={errorText || warningText}
                    FormHelperTextProps={{ error: !!(errorText || warningText) }}
                />
            )}
        />
    );
}
